import React, { useEffect, useState, useRef, Fragment, useCallback } from 'react';

import _ from 'lodash';
import cx from 'classnames';
import { Dropdown, Form, InputGroup } from 'react-bootstrap';

import { Fonts } from '@paperflite-marketing/cleverstory-commons';
import { getColorsConfig } from '../../../../AssetViewer/helper/cssHelper';
import { FormWidgetContext } from '../..';
import TruncatedTextWithTooltip from '../../../../TruncatedTextWithTooltip/index';
import { injectAktivFallback } from '../../../../widgets/widget.helper';

import styles from './dropdown.module.scss'

const FormDropdownList = React.memo(function DropDownListEditor({
  className,
  style,
  widgetProps,
  show = false,
  breakpoint
}) {
  const context = React.useContext(FormWidgetContext);

  const dropdownRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [checkedItems, setCheckedItems] = useState([]);
  const [errors, setErrors] = useState('');

  const id = _.get(widgetProps, 'uid')
  const properties = _.get(widgetProps, 'properties', {});
  const label = _.get(widgetProps, 'properties.desktop.label') || '';
  const multiSelect = _.get(widgetProps, "properties.desktop.multiSelect") || false;
  const field = _.get(widgetProps, 'properties.desktop.field') || '';
  const fieldId = _.get(widgetProps, 'content.id') || "";
  const listOptions = _.get(widgetProps, 'content.options', []);
  const error = _.get(context.errors, id);

  useEffect(() => {
    setErrors('');
    const values = checkedItems.map(item => item.id);
    context.updateField(id, field, fieldId, values)
  }, [checkedItems])

  const handleInputChange = (selectedOption) => {    
    setCheckedItems(prevState => {
      if(multiSelect) {
        const alreadyExist = prevState.some(item => item.id === selectedOption.id);
        if(alreadyExist) {
          return prevState.filter(item => item.id !== selectedOption.id);
        }else {
          return _.uniqBy([...prevState, selectedOption], 'id');
        }
      }else {
        setIsOpen(false);
        return [selectedOption];
      }
    });
  };

  useEffect(()=>{
    setErrors(error);
  }, [error])

  useEffect(()=>{
    setIsOpen(show);
  },[show]);

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target) && !show) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [show]);

  Fonts.useFont(_.get(widgetProps, 'fonts') || [])

  var labelStyles = getLabelStyles(properties, breakpoint, 'regular')
  var inputStyles = getInputStyles(properties, breakpoint, 'regular')

  const dropdownMenuStyles = getDropDownMenuStyles(properties, breakpoint);

  return (
    <div
      id={id}
      className={cx(className, styles.dropDownContainer, {[styles.overlap]: isOpen})}
      style={style}
      data-widget-id={id}
    >
      {label && <label style={labelStyles}>{label}</label>}
      <Dropdown className={styles.customDropdown} show={isOpen} ref={dropdownRef}>
        <Dropdown.Toggle 
          id={`dropdown_button_${id}`} 
          as={CustomToggle} 
          onClick={() => setIsOpen(!isOpen)} 
          checkedItems={checkedItems} 
          setCheckedItems={setCheckedItems} 
          inputStyles={inputStyles} 
          properties={properties} 
          isOpen={isOpen} 
          multiSelect={multiSelect}
          tabIndex={0}
        />
        {isOpen &&
          <Dropdown.Menu
            className={styles.customOptionWrapper}
            align='end'
            style={dropdownMenuStyles}
            as={CustomMenu}
            show={isOpen}
          >
          {_.map(listOptions, (option) => {
            const isChecked = _.some(checkedItems, { id: option.id });
              return (
                <Dropdown.Item 
                  key={option.id} 
                  id={option.id} 
                  as={CheckDropdownItem} 
                  multiSelect={multiSelect} 
                  checked={isChecked} 
                  onChange={() => handleInputChange(option)}
                >
                  {option.value}
                </Dropdown.Item>
              )
            })}
          </Dropdown.Menu>
        }  
      </Dropdown>
      <span className={cx(styles.error, { [styles.active]: errors })}>
        {errors}
      </span>
    </div>
  )
})

const CustomToggle = React.forwardRef(({checkedItems, setCheckedItems, isOpen, properties, inputStyles, multiSelect, onClick, tabIndex }, ref) => {
  const placeholder = _.get(properties, 'desktop.placeholder') || '';
  const placeholderColor = getPlaceholderColor(properties);
  return(
    <div
      ref={ref}
      style={inputStyles}
      className={cx(styles.toggleButton)}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick(e);
      }}
      tabIndex={tabIndex}
    >
      <div className={styles.actionConatiner}>
        <div className={styles.selectedFields}>
          {_.size(checkedItems) > 0 ? (
              <RenderSelectedOptions selectedOptions={checkedItems.map(option => option.value)}/>
          ) : (
            <span style={placeholderColor} className={styles.placeholder}>
              {placeholder}
            </span>
          )}
        </div>
        {
          _.size(checkedItems) > 0 && multiSelect &&
          <span className={styles.clearBtn} onClick={() => setCheckedItems([])}>
            <i className={cx('cleverstory-close')} />
          </span>
        }
        <span className={cx(styles.dropDownIndicator, {[styles.open]: isOpen})}>
          <i
            className={cx('cleverstory-drop-down', styles.dropDownIcon, {
              [styles.showDropdown]: false
            })}
          />
        </span>
      </div>
    </div>
  )}
)

const CustomMenu = React.forwardRef(({ children, style, className, show }, ref) => {
  const [searchText, setSearchText] = React.useState('');

  return (
    <div style={style} className={cx(className,{[styles.displayMenu] :show})} ref={ref}>
      <SearchBox searchText={searchText} setSearchText={setSearchText}/>
      {React.Children.toArray(children).filter(
        (child) =>
          !searchText || child.props.children.toLowerCase().includes(searchText.toLowerCase())
      )}
    </div>
  )
})

const CheckDropdownItem = React.forwardRef(
  ({ children, id, checked, onChange, multiSelect }, ref) => {
    return (
      <Form.Group key={id} ref={ref} className={cx(styles.checkBoxItem)} controlId={id}>
        <Form.Check
          type={'checkbox'}
          label={children}
          checked={checked}
          className={cx({[styles.singleSelect] : !multiSelect})}
          onChange={onChange}
        />
      </Form.Group>
    )
  }
)

const SearchBox = React.memo(({ searchText, setSearchText }) => {
  return (
    <InputGroup className={styles.searchBox}>
      <InputGroup.Text className={styles.searchIcon} id='basic-addon1'>
        <i className={cx('cleverstory-search')} />
      </InputGroup.Text>
      <Form.Control
        className={styles.field}
        placeholder='Search'
        onChange={(e) => setSearchText(e.target.value)}
        value={searchText}
      />
    </InputGroup>
  )
})

const RenderSelectedOptions = ({ selectedOptions }) => {
  const containerRef = useRef(null);
  const [content, setContent] = useState({ 
    visibleOptions: [], 
    remainingCount: 0 
  });

  const calculateVisibleOptions = useCallback(() => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth - 30;
      let totalWidth = 0;
      let lastIndex = 0;

      selectedOptions.forEach((string, index) => {
        const stringWidth = document.createElement('span');
        stringWidth.style.visibility = 'hidden';
        stringWidth.style.position = 'absolute';
        stringWidth.innerText = string;
        document.body.appendChild(stringWidth);

        let width = stringWidth.offsetWidth;
        if (width > 150) {
          width = 150;
        }
        document.body.removeChild(stringWidth);

        if (totalWidth + width > containerWidth) {
          if (index === 0) {
            totalWidth = containerWidth;
            lastIndex = 1;
          }
          return;
        }
        totalWidth += width;
        lastIndex = index + 1;
      });

      setContent({
        visibleOptions: selectedOptions.slice(0, lastIndex),
        remainingCount: selectedOptions.length - lastIndex,
      });
    }
  }, [selectedOptions]);

  useEffect(() => {
    calculateVisibleOptions();
  }, [selectedOptions, calculateVisibleOptions]);

  return (
    <div ref={containerRef} className={styles.inputContainer}>
      {content.visibleOptions.map((value, index, allOptions) => (
        <React.Fragment key={index}>
          <TruncatedTextWithTooltip 
            className={styles.selected} 
            content={value} 
            maxWidth={allOptions.length === 1 ? containerRef.current.offsetWidth - 30 : 150} 
            placement="top" 
          />
          {index === allOptions.length - 1 ? `  ` : `, `}
        </React.Fragment>
      ))}
      {_.size(selectedOptions) > 1 && content.remainingCount > 0 && 
        <span className={styles.count}>+{content.remainingCount}</span>
      }
    </div>
  );
}

/**
 * --------------------------------------------------
 * Label Styles
 * --------------------------------------------------
 */
function getLabelStyles(properties, breakpoint, active) {
  var styles = {}

  if (!properties) {
    return styles
  }

  const defaultProperties = _.get(properties, `desktop`)
  const deviceProperties = _.get(properties, `${breakpoint}`)
  const activeStyles = _.get(defaultProperties, `${active}`, {})

  const alignment = _.get(deviceProperties, 'styles.layout.alignment') || 'left'

  const fieldSpacing = _.get(deviceProperties, 'styles.layout.fieldSpacing') || 0;
  styles['paddingBottom'] = `${fieldSpacing}px`

  const labelPadding = _.get(deviceProperties, 'styles.layout.labelPadding') || 0;
  if (alignment === 'left') {
    styles['paddingLeft'] = `${labelPadding}px`
  } else if (alignment === 'right') {
    styles['paddingRight'] = `${labelPadding}px`
  }

  const inputStyles = _.get(defaultProperties, 'styles.regular.label')
  const fontStyles = getFontStyle(inputStyles)

  styles = {
    ...styles,
    ...fontStyles
  }

  return styles
}

/**
 * --------------------------------------------------
 * Input Styles
 * --------------------------------------------------
 */
function getInputStyles(properties, breakpoint, active) {
  var styles = {}

  if (!properties) {
    return styles
  }

  const defaultProperties = _.get(properties, `desktop`)
  const deviceProperties = _.get(properties, `${breakpoint}`)

  const inputPadding = _.get(deviceProperties, 'styles.layout.inputPadding') || 0

  styles['padding'] = `0px ${inputPadding}px`

  const inputStyles = _.get(defaultProperties, 'styles.regular.input')
  const fontStyles = getFontStyle(inputStyles)

  styles = {
    ...styles,
    ...fontStyles
  }

  const border = _.get(defaultProperties, 'styles.regular.border')
  const borderStyles = getBorderStyles(border)

  styles = {
    ...styles,
    ...borderStyles
  }

  return styles
}

/**
 * --------------------------------------------------
 * DropDown Menu Styles
 * --------------------------------------------------
 */
function getDropDownMenuStyles(properties, breakpoint) {
  var styles = {}

  if (!properties) {
    return styles
  }

  const defaultProperties = _.get(properties, `desktop`)
  const deviceProperties = _.get(properties, `${breakpoint}`);
  const listLayoutStyles = _.get(deviceProperties, 'styles.layout')

  const verticalPadding = _.get(listLayoutStyles, 'verticalPadding') || 4
  const horizontalPadding = _.get(listLayoutStyles, 'horizontalPadding') || 4
  const spaceBetweenItems = _.get(listLayoutStyles, 'spaceBetweenItems') || 2

  styles = {
    ...styles,
    display: 'grid',
    gridGap: `${spaceBetweenItems}px`,
    padding: `${verticalPadding}px ${horizontalPadding}px`
  }

  const border = _.get(defaultProperties, 'styles.openProperty.border');
  const borderStyles = getBorderStyles(border)

  styles = {
    ...styles,
    ...borderStyles
  }

    //background-color
    const backgroundColorRgb = _.get(defaultProperties, 'styles.openProperty.background', null)
    if (backgroundColorRgb) {
      styles = {
        ...styles,
        background: getColorsConfig(backgroundColorRgb)
      }
    }
  
    //font-color
    const colorRgb = _.get(defaultProperties, 'styles.openProperty.text', null)
    const color = getColorStyles(colorRgb)
    styles = {
      ...styles,
      ...color
    }
  

  return styles;
}

/**
 * --------------------------------------------------
 * Placeholder Styles
 * --------------------------------------------------
 */
export function getPlaceholderColor(properties) {
  var styles = {}

  if (!properties) {
    return styles
  }

  const defaultProperties = _.get(properties, `desktop`)
  const inputStyles = _.get(defaultProperties, 'styles.regular.input')

  const colorRgb = _.get(inputStyles, 'placeholderColor', null)
  if (!colorRgb) {
    return null
  }

  return {
    color: getColorsConfig(colorRgb)
  }
}

export function getColorStyles(value) {
  if (!value) {
    return null
  }

  const colorRgb = value

  var color = 'inherit'
  if (colorRgb) {
    color = getColorsConfig(colorRgb)
  }

  var styles = {
    color: color
  }

  return styles
}

export function getBorderStyles(border) {
  var styles = {}
  if (!border) {
    return styles
  }

  const topLeft = _.get(border, 'radius.topLeft', null)
  const topRight = _.get(border, 'radius.topRight', null)
  const bottomLeft = _.get(border, 'radius.bottomLeft', null)
  const bottomRight = _.get(border, 'radius.bottomRight', null)

  if (topLeft) {
    styles['borderTopLeftRadius'] = parseInt(topLeft) + 'px'
  }

  if (topRight) {
    styles['borderTopRightRadius'] = parseInt(topRight) + 'px'
  }

  if (bottomLeft) {
    styles['borderBottomLeftRadius'] = parseInt(bottomLeft) + 'px'
  }

  if (bottomRight) {
    styles['borderBottomRightRadius'] = parseInt(bottomRight) + 'px'
  }

  const borderStyle = _.get(border, 'style') || 'bottom'
  if (borderStyle !== 'none') {
    const borderWidthString = _.get(border, 'width') || 0
    var borderWidget = parseInt(borderWidthString) + 'px'

    const fieldName = borderStyle == 'all' ? 'borderWidth' : 'borderBottomWidth'
    styles[fieldName] = borderWidget

    const colorRgb = _.get(border, 'color')
    var color = 'transparent'
    if (colorRgb) {
      color = getColorsConfig(colorRgb)
    }

    styles['borderColor'] = color
  }

  return styles
}

export function getFontStyle(fontStyles) {
  var styles = {}

  if (!fontStyles) {
    return styles
  }

  //background-color
  const backgroundColorRgb = _.get(fontStyles, 'background', null)
  if (backgroundColorRgb) {
    styles = {
      ...styles,
      background: getColorsConfig(backgroundColorRgb)
    }
  }

  //font-color
  const colorRgb = _.get(fontStyles, 'color', null)
  const color = getColorStyles(colorRgb)
  styles = {
    ...styles,
    ...color
  }

  //font-family
  const fontFamily = _.get(fontStyles, 'font.fontFamily', 'inherit')
  styles = {
    ...styles,
    fontFamily: injectAktivFallback(fontFamily)
  }

  //font-size
  const fontSizeString = _.get(fontStyles, `font.fontSize`) || 14
  if (fontSizeString) {
    var fontSize = parseInt(fontSizeString) + 'px'
    styles['fontSize'] = fontSize
  }

  return styles
}

export { FormDropdownList }